

import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Link,
    Button
} from "@mui/material";

import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import ConfirmDialog from "../../Common/ConfirmDialog";


// ============================================================
// GET SVG FROM HIGHCHARTS INSTANCE
// ============================================================
// Uses the actual Highcharts instance exposed by highcharts-react-official.
// Falls back to a temporary off-screen chart when graphs are hidden/unmounted.
const getChartSvg = (chartRef, options = {}, width = 1200, height = 600) => {
    try {
        const chart = chartRef?.current?.chart;

        if (chart && typeof chart.getSVG === "function") {
            return chart.getSVG({ chart: { width, height } });
        }

        if (!options || typeof Highcharts?.chart !== "function") {
            return null;
        }

        const container = document.createElement("div");
        container.style.position = "fixed";
        container.style.left = "-100000px";
        container.style.top = "0";
        container.style.width = `${width}px`;
        container.style.height = `${height}px`;
        container.style.visibility = "hidden";
        document.body.appendChild(container);

        let temporaryChart = null;
        try {
            temporaryChart = Highcharts.chart(container, {
                ...options,
                chart: {
                    ...(options.chart || {}),
                    width,
                    height,
                    animation: false
                }
            });

            return temporaryChart?.getSVG?.({ chart: { width, height } }) || null;
        } finally {
            if (temporaryChart) temporaryChart.destroy();
            container.remove();
        }
    } catch (error) {
        console.error("Unable to get Highcharts SVG:", error);
        return null;
    }
};

// ============================================================
// CONVERT SVG TO PNG
// ============================================================
const svgToPngDataUrl = (svgString, width = 1200, height = 600) => {
    return new Promise((resolve, reject) => {
        if (!svgString) {
            reject(new Error("SVG data is empty"));
            return;
        }

        try {
            const parser = new DOMParser();
            const svgDocument = parser.parseFromString(svgString, "image/svg+xml");
            const svgElement = svgDocument.documentElement;

            if (!svgElement || svgElement.nodeName.toLowerCase() !== "svg") {
                reject(new Error("Invalid SVG returned by Highcharts"));
                return;
            }

            svgElement.setAttribute("width", String(width));
            svgElement.setAttribute("height", String(height));
            svgElement.setAttribute("xmlns", "http://www.w3.org/2000/svg");
            svgElement.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");

            if (!svgElement.getAttribute("viewBox")) {
                svgElement.setAttribute("viewBox", `0 0 ${width} ${height}`);
            }

            const serializedSvg = new XMLSerializer().serializeToString(svgElement);
            const svgBlob = new Blob([serializedSvg], { type: "image/svg+xml;charset=utf-8" });
            const url = URL.createObjectURL(svgBlob);
            const image = new Image();

            image.onload = () => {
                try {
                    const canvas = document.createElement("canvas");
                    canvas.width = width;
                    canvas.height = height;
                    const context = canvas.getContext("2d");

                    if (!context) throw new Error("Unable to create canvas context");

                    context.fillStyle = "#ffffff";
                    context.fillRect(0, 0, width, height);
                    context.drawImage(image, 0, 0, width, height);

                    const pngDataUrl = canvas.toDataURL("image/png");
                    URL.revokeObjectURL(url);
                    resolve(pngDataUrl);
                } catch (error) {
                    URL.revokeObjectURL(url);
                    reject(error);
                }
            };

            image.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error("Unable to convert Highcharts SVG to PNG"));
            };

            image.src = url;
        } catch (error) {
            reject(error);
        }
    });
};

// HELPER: NUMBER
// ================================================================

const toNumber = value => {

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return null;
    }

    const number = Number(value);

    return Number.isFinite(number)
        ? number
        : null;
};


// ================================================================
// HELPER: RESPONSE VALUES
// ================================================================

const parseResponseValues = value => {

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return [];
    }

    if (Array.isArray(value)) {

        return value
            .map(item => toNumber(item))
            .filter(
                item => item !== null
            );

    }

    return String(value)
        .split(",")
        .map(
            item =>
                toNumber(
                    item.trim()
                )
        )
        .filter(
            item => item !== null
        );

};


// ================================================================
// COMPONENT
// ================================================================

export default function History({
    resultDataMap,
    setResultDataMap,
    resultFields = [],
    rows = [],
    calibrationRows = [],
    showHistory = true,
    showCalibrationGraph = true,
    onPredict
}) {

    // ============================================================
    // STATE
    // ============================================================

    const [
        selectedRun,
        setSelectedRun
    ] = useState(null);

    const [
        graphVisible,
        setGraphVisible
    ] = useState(true);

    const [
        openClearDialog,
        setOpenClearDialog
    ] = useState(false);


    // ============================================================
    // CHART REFERENCES
    // ============================================================

    const responseChartRef =
        useRef(null);

    const predictionChartRef =
        useRef(null);


    // ============================================================
    // HISTORY DATA
    // ============================================================

    const historyData = useMemo(() => {

        return Object.values(
            resultDataMap || {}
        );

    }, [resultDataMap]);


    // ============================================================
    // SELECT LATEST RUN
    // ============================================================

    useEffect(() => {

        if (!historyData.length) {

            setSelectedRun(null);

            return;
        }

        const latest =
            [...historyData].sort(
                (a, b) =>
                    Number(
                        b?.run ??
                        b?.runId ??
                        b?.id ??
                        0
                    ) -
                    Number(
                        a?.run ??
                        a?.runId ??
                        a?.id ??
                        0
                    )
            )[0];

        setSelectedRun(
            latest?.run ??
            latest?.runId ??
            latest?.id ??
            null
        );

    }, [historyData]);


    // ============================================================
    // SELECTED ENTRY
    // ============================================================

    const selectedEntry = useMemo(() => {

        if (
            selectedRun === null ||
            selectedRun === undefined
        ) {
            return (
                historyData[
                historyData.length - 1
                ] || null
            );
        }

        return (
            historyData.find(
                item =>
                    String(
                        item?.run ??
                        item?.runId ??
                        item?.id
                    ) ===
                    String(selectedRun)
            ) || null
        );

    }, [
        historyData,
        selectedRun
    ]);


    // ============================================================
    // RESULT
    // ============================================================

    const selectedResult =
        selectedEntry?.calibrationResponse ||
        selectedEntry?.result ||
        selectedEntry?.resultData ||
        null;


    // ============================================================
    // PREDICTIONS
    // ============================================================

    const predictions = useMemo(() => {

        if (
            Array.isArray(
                selectedResult?.predictions
            )
        ) {
            return selectedResult.predictions;
        }

        if (
            Array.isArray(
                selectedResult?.prediction
            )
        ) {
            return selectedResult.prediction;
        }

        if (
            Array.isArray(
                selectedEntry?.predictions
            )
        ) {
            return selectedEntry.predictions;
        }

        return [];

    }, [
        selectedResult,
        selectedEntry
    ]);


    // ============================================================
    // CALIBRATION ROWS
    // ============================================================

    const graphCalibrationRows =
        useMemo(() => {

            if (
                Array.isArray(
                    calibrationRows
                ) &&
                calibrationRows.length
            ) {
                return calibrationRows;
            }

            if (
                Array.isArray(
                    selectedEntry?.rows
                ) &&
                selectedEntry.rows.length
            ) {
                return selectedEntry.rows;
            }

            return Array.isArray(rows)
                ? rows
                : [];

        }, [
            calibrationRows,
            selectedEntry,
            rows
        ]);


    // ============================================================
    // GET SAV
    // ============================================================

    const getCalibrationSAV =
        row => {

            const candidates = [

                row?.sav,

                row?.SAV,

                row?.fSAV,

                row?.fSav,

                row?.concentration,

                row?.Concentration

            ];

            for (
                const value of candidates
            ) {

                const number =
                    toNumber(value);

                if (
                    number !== null
                ) {
                    return number;
                }

            }

            return null;
        };


    // ============================================================
    // SAMPLE ID
    // ============================================================

    const getSampleId =
        (prediction, index) => {

            return (
                prediction?.sampleId ??
                prediction?.sampleID ??
                prediction?.sample ??
                prediction?.SampleID ??
                `Sample ${index + 1} `
            );

        };


    // ============================================================
    // CONDITION CODE
    // ============================================================

    const getConditionCode =
        prediction => {

            return (
                prediction?.conditionCode ??
                prediction?.ConditionCode ??
                prediction?.condition ??
                ""
            );

        };


    // ============================================================
    // RESPONSE GRAPH DATA
    // ============================================================

    const responseGraphData =
        useMemo(() => {

            const calibrationResp1 = [];
            const calibrationResp2 = [];

            const predictionResp1 = [];
            const predictionResp2 = [];


            // ----------------------------------------------------
            // CALIBRATION
            // ----------------------------------------------------

            graphCalibrationRows.forEach(
                (row, rowIndex) => {

                    const sav =
                        getCalibrationSAV(
                            row
                        );

                    if (
                        sav === null
                    ) {
                        return;
                    }


                    const resp1 =
                        parseResponseValues(
                            row?.resp1 ??
                            row?.Response1 ??
                            row?.response1
                        );


                    resp1.forEach(
                        (response, index) => {

                            calibrationResp1.push({

                                x: response,

                                y: sav,

                                source:
                                    "Calibration",

                                responseType:
                                    "Resp1",

                                timeline:
                                    `Calibration ${rowIndex + 1}.${index + 1} `

                            });

                        }
                    );


                    const resp2 =
                        parseResponseValues(
                            row?.resp2 ??
                            row?.Response2 ??
                            row?.response2
                        );


                    resp2.forEach(
                        (response, index) => {

                            calibrationResp2.push({

                                x: response,

                                y: sav,

                                source:
                                    "Calibration",

                                responseType:
                                    "Resp2",

                                timeline:
                                    `Calibration ${rowIndex + 1}.${index + 1} `

                            });

                        }
                    );

                }
            );


            // ----------------------------------------------------
            // PREDICTION
            // ----------------------------------------------------

            predictions.forEach(
                (prediction, index) => {

                    const concentration =
                        toNumber(
                            prediction?.concentration
                        );

                    if (
                        concentration === null
                    ) {
                        return;
                    }


                    const sampleId =
                        getSampleId(
                            prediction,
                            index
                        );

                    const conditionCode =
                        getConditionCode(
                            prediction
                        );


                    const response1 =
                        toNumber(
                            prediction?.response1 ??
                            prediction?.Response1 ??
                            prediction?.resp3 ??
                            prediction?.Resp3
                        );


                    if (
                        response1 !== null
                    ) {

                        predictionResp1.push({

                            x: response1,

                            y: concentration,

                            source:
                                "Prediction",

                            responseType:
                                "Resp3",

                            sampleId,

                            conditionCode,

                            timeline:
                                `Prediction ${index + 1} `

                        });

                    }


                    const response2 =
                        toNumber(
                            prediction?.response2 ??
                            prediction?.Response2 ??
                            prediction?.resp4 ??
                            prediction?.Resp4
                        );


                    if (
                        response2 !== null
                    ) {

                        predictionResp2.push({

                            x: response2,

                            y: concentration,

                            source:
                                "Prediction",

                            responseType:
                                "Resp4",

                            sampleId,

                            conditionCode,

                            timeline:
                                `Prediction ${index + 1} `

                        });

                    }

                }
            );


            return {

                calibrationResp1,

                calibrationResp2,

                predictionResp1,

                predictionResp2

            };

        }, [
            graphCalibrationRows,
            predictions
        ]);


    // ============================================================
    // RESPONSE GRAPH SERIES
    // ============================================================

    const responseSeries =
        useMemo(() => {

            const series = [];


            if (
                responseGraphData
                    .calibrationResp1
                    .length
            ) {

                series.push({

                    name:
                        "Resp1 - Calibration",

                    type:
                        "scatter",

                    data:
                        responseGraphData
                            .calibrationResp1,

                    marker: {

                        symbol:
                            "circle",

                        radius:
                            6

                    }

                });

            }


            if (
                responseGraphData
                    .calibrationResp2
                    .length
            ) {

                series.push({

                    name:
                        "Resp2 - Calibration",

                    type:
                        "scatter",

                    data:
                        responseGraphData
                            .calibrationResp2,

                    marker: {

                        symbol:
                            "diamond",

                        radius:
                            6

                    }

                });

            }


            if (
                responseGraphData
                    .predictionResp1
                    .length
            ) {

                series.push({

                    name:
                        "Resp3 - Prediction",

                    type:
                        "scatter",

                    data:
                        responseGraphData
                            .predictionResp1,

                    marker: {

                        symbol:
                            "triangle",

                        radius:
                            7

                    }

                });

            }


            if (
                responseGraphData
                    .predictionResp2
                    .length
            ) {

                series.push({

                    name:
                        "Resp4 - Prediction",

                    type:
                        "scatter",

                    data:
                        responseGraphData
                            .predictionResp2,

                    marker: {

                        symbol:
                            "square",

                        radius:
                            6

                    }

                });

            }


            return series;

        }, [
            responseGraphData
        ]);


    // ============================================================
    // RESPONSE GRAPH OPTIONS
    // ============================================================

    const responseGraphOptions =
        useMemo(() => {

            return {

                chart: {

                    type:
                        "scatter",

                    height:
                        520,

                    animation:
                        false

                },

                title: {

                    text:
                        "Response vs SAV / Concentration"

                },

                credits: {

                    enabled:
                        false

                },

                xAxis: {

                    title: {

                        text:
                            "Response Values"

                    },

                    gridLineWidth:
                        1

                },

                yAxis: {

                    title: {

                        text:
                            "SAV / Concentration"

                    },

                    gridLineWidth:
                        1

                },

                legend: {

                    enabled:
                        true

                },

                tooltip: {

                    useHTML:
                        true,

                    formatter:
                        function () {

                            const point =
                                this.point;

                            let html =
                                `< b > ${this.series.name}</b > <br />`;

                            html +=
                                `Response: ${point.x} <br />`;

                            html +=
                                `SAV / Concentration: ${point.y} <br />`;

                            if (
                                point.sampleId
                            ) {

                                html +=
                                    `Sample ID: ${point.sampleId} <br />`;

                            }

                            if (
                                point.conditionCode
                            ) {

                                html +=
                                    `Condition Code: ${point.conditionCode} <br />`;

                            }

                            if (
                                point.timeline
                            ) {

                                html +=
                                    `Timeline: ${point.timeline} <br />`;

                            }

                            return html;

                        }

                },

                plotOptions: {

                    scatter: {

                        turboThreshold:
                            0,

                        animation:
                            false

                    }

                },

                series:
                    responseSeries

            };

        }, [
            responseSeries
        ]);


    // ============================================================
    // PREDICTION SAMPLE GRAPH DATA
    // ============================================================

    const predictionSampleGraphData =
        useMemo(() => {

            return predictions
                .map(
                    (prediction, index) => {

                        const concentration =
                            toNumber(
                                prediction?.concentration
                            );

                        const response1 =
                            toNumber(
                                prediction?.response1 ??
                                prediction?.Response1 ??
                                prediction?.resp3 ??
                                prediction?.Resp3
                            );

                        const response2 =
                            toNumber(
                                prediction?.response2 ??
                                prediction?.Response2 ??
                                prediction?.resp4 ??
                                prediction?.Resp4
                            );

                        return {

                            index:
                                index + 1,

                            sampleId:
                                getSampleId(
                                    prediction,
                                    index
                                ),

                            response1,

                            response2,

                            concentration,

                            conditionCode:
                                getConditionCode(
                                    prediction
                                ),

                            timeline:
                                `Prediction ${index + 1} `

                        };

                    }
                )
                .filter(
                    item =>
                        item.concentration !== null ||
                        item.response1 !== null ||
                        item.response2 !== null
                );

        }, [
            predictions
        ]);


    // ============================================================
    // PREDICTION RESPONSE 1
    // ============================================================

    const predictionResponse1Data =
        useMemo(() => {

            return predictionSampleGraphData
                .filter(
                    item =>
                        item.response1 !== null &&
                        item.concentration !== null
                )
                .map(item => ({

                    x:
                        item.index - 1,

                    y:
                        item.concentration,

                    response:
                        item.response1,

                    sampleId:
                        item.sampleId,

                    conditionCode:
                        item.conditionCode,

                    timeline:
                        item.timeline

                }));

        }, [
            predictionSampleGraphData
        ]);


    // ============================================================
    // PREDICTION RESPONSE 2
    // ============================================================

    const predictionResponse2Data =
        useMemo(() => {

            return predictionSampleGraphData
                .filter(
                    item =>
                        item.response2 !== null &&
                        item.concentration !== null
                )
                .map(item => ({

                    x:
                        item.index - 1,

                    y:
                        item.concentration,

                    response:
                        item.response2,

                    sampleId:
                        item.sampleId,

                    conditionCode:
                        item.conditionCode,

                    timeline:
                        item.timeline

                }));

        }, [
            predictionSampleGraphData
        ]);


    // ============================================================
    // PREDICTION GRAPH OPTIONS
    // ============================================================

    const predictionSampleGraphOptions =
        useMemo(() => {

            const categories =
                predictionSampleGraphData
                    .map(
                        item =>
                            item.sampleId
                    );


            const series = [];


            if (
                predictionResponse1Data.length
            ) {

                series.push({

                    name:
                        "Resp3 / Response 1",

                    type:
                        "scatter",

                    data:
                        predictionResponse1Data,

                    marker: {

                        symbol:
                            "circle",

                        radius:
                            6

                    }

                });

            }


            if (
                predictionResponse2Data.length
            ) {

                series.push({

                    name:
                        "Resp4 / Response 2",

                    type:
                        "scatter",

                    data:
                        predictionResponse2Data,

                    marker: {

                        symbol:
                            "diamond",

                        radius:
                            6

                    }

                });

            }


            return {

                chart: {

                    type:
                        "scatter",

                    height:
                        480,

                    animation:
                        false

                },

                title: {

                    text:
                        "Prediction Sample ID vs Concentration"

                },

                credits: {

                    enabled:
                        false

                },

                xAxis: {

                    categories,

                    title: {

                        text:
                            "Sample ID"

                    },

                    labels: {

                        rotation:
                            -45,

                        formatter:
                            function () {

                                return (
                                    categories[
                                    this.value
                                    ] || ""
                                );

                            }

                    }

                },

                yAxis: {

                    title: {

                        text:
                            "Concentration"

                    },

                    gridLineWidth:
                        1

                },

                legend: {

                    enabled:
                        true

                },

                tooltip: {

                    useHTML:
                        true,

                    formatter:
                        function () {

                            const point =
                                this.point;

                            let html =
                                `< b > ${this.series.name}</b > <br />`;

                            html +=
                                `Sample ID: ${point.sampleId} <br />`;

                            html +=
                                `Response: ${point.response} <br />`;

                            html +=
                                `Concentration: ${point.y} <br />`;

                            if (
                                point.conditionCode
                            ) {

                                html +=
                                    `Condition Code: ${point.conditionCode} <br />`;

                            }

                            if (
                                point.timeline
                            ) {

                                html +=
                                    `Timeline: ${point.timeline} <br />`;

                            }

                            return html;

                        }

                },

                plotOptions: {

                    scatter: {

                        turboThreshold:
                            0,

                        animation:
                            false

                    }

                },

                series

            };

        }, [
            predictionSampleGraphData,
            predictionResponse1Data,
            predictionResponse2Data
        ]);


    // ============================================================
    // CALIBRATION INPUT EXPORT DATA
    // ============================================================

    const calibrationInputData =
        useMemo(() => {

            return graphCalibrationRows.map(
                (row, index) => {

                    const sav =
                        row?.sav ??
                        row?.SAV ??
                        row?.fSAV ??
                        row?.fSav ??
                        row?.concentration ??
                        row?.Concentration ??
                        "";

                    const resp1 =
                        row?.resp1 ??
                        row?.Response1 ??
                        row?.response1 ??
                        "";

                    const resp2 =
                        row?.resp2 ??
                        row?.Response2 ??
                        row?.response2 ??
                        "";

                    return {

                        Level:
                            index + 1,

                        SAV:
                            sav,

                        Resp1:
                            Array.isArray(resp1)
                                ? resp1.join(", ")
                                : resp1,

                        Resp2:
                            Array.isArray(resp2)
                                ? resp2.join(", ")
                                : resp2

                    };

                }
            );

        }, [
            graphCalibrationRows
        ]);


    // ============================================================
    // CALIBRATION API RESPONSE
    // ============================================================
    // Display the actual calibration API response stored in history.
    // This does not depend on resultFields, so newly returned API fields are included.
    const calibrationResultData = useMemo(() => {
        const apiResponse =
            selectedEntry?.calibrationResponse ||
            selectedEntry?.result ||
            selectedEntry?.resultData ||
            selectedResult;

        if (!apiResponse || typeof apiResponse !== "object") return [];

        const excludedFields = new Set([
            "predictions",
            "prediction",
            "rows",
            "calibrationRows"
        ]);

        return Object.entries(apiResponse)
            .filter(([key, value]) =>
                !excludedFields.has(key) &&
                value !== undefined &&
                value !== null
            )
            .map(([key, value]) => ({
                Parameter: key,
                Value: value
            }));
    }, [selectedEntry, selectedResult]);

    // PREDICTION EXPORT DATA
    // ============================================================

    const predictionExportData =
        useMemo(() => {

            return predictions.map(
                (prediction, index) => {

                    const response1 =
                        prediction?.response1 ??
                        prediction?.Response1 ??
                        prediction?.resp3 ??
                        prediction?.Resp3 ??
                        "";

                    const response2 =
                        prediction?.response2 ??
                        prediction?.Response2 ??
                        prediction?.resp4 ??
                        prediction?.Resp4 ??
                        "";

                    return {

                        SampleID:
                            getSampleId(
                                prediction,
                                index
                            ),

                        Resp3:
                            response1,

                        Resp4:
                            response2,

                        Concentration:
                            prediction?.concentration ??
                            ""

                    };

                }
            );

        }, [
            predictions
        ]);


    // ============================================================
    // CLEAR HISTORY
    // ============================================================

    const clearHistory = () => {

        sessionStorage.removeItem(
            "microslideHistory_calibrate"
        );

        if (
            setResultDataMap
        ) {

            setResultDataMap({});

        }

        setSelectedRun(null);

        setGraphVisible(false);

        setOpenClearDialog(false);

    };


    // ============================================================
    // PREDICT
    // ============================================================

    const handlePredict =
        entry => {

            if (onPredict) {

                onPredict(
                    entry
                );

            }

        };


    // ============================================================
    // EXPORT EXCEL
    // ============================================================

    const exportExcel =
        async () => {

            try {

                const workbook =
                    new ExcelJS.Workbook();


                workbook.creator =
                    "LIMS Portal";


                workbook.created =
                    new Date();


                // =================================================
                // CALIBRATION INPUT
                // =================================================

                const calibrationSheet =
                    workbook.addWorksheet(
                        "Calibration Input"
                    );


                calibrationSheet.columns = [

                    {
                        header:
                            "Level",

                        key:
                            "Level",

                        width:
                            14

                    },

                    {
                        header:
                            "SAV",

                        key:
                            "SAV",

                        width:
                            20

                    },

                    {
                        header:
                            "Resp1",

                        key:
                            "Resp1",

                        width:
                            45

                    },

                    {
                        header:
                            "Resp2",

                        key:
                            "Resp2",

                        width:
                            45

                    }

                ];


                calibrationInputData.forEach(
                    row => {

                        calibrationSheet.addRow(
                            row
                        );

                    }
                );


                // =================================================
                // CALIBRATION RESULTS
                // =================================================

                const calibrationResultSheet =
                    workbook.addWorksheet(
                        "Calibration Results"
                    );


                calibrationResultSheet.columns = [

                    {
                        header:
                            "Parameter",

                        key:
                            "Parameter",

                        width:
                            32

                    },

                    {
                        header:
                            "Value",

                        key:
                            "Value",

                        width:
                            40

                    }

                ];


                calibrationResultData.forEach(
                    row => {

                        calibrationResultSheet.addRow(
                            row
                        );

                    }
                );


                // =================================================
                // PREDICTION DATA
                // =================================================

                const predictionSheet =
                    workbook.addWorksheet(
                        "Prediction Data"
                    );


                predictionSheet.columns = [

                    {
                        header:
                            "Sample ID",

                        key:
                            "SampleID",

                        width:
                            24

                    },

                    {
                        header:
                            "Resp3",

                        key:
                            "Resp3",

                        width:
                            24

                    },

                    {
                        header:
                            "Resp4",

                        key:
                            "Resp4",

                        width:
                            24

                    },

                    {
                        header:
                            "Concentration",

                        key:
                            "Concentration",

                        width:
                            24

                    }

                ];


                predictionExportData.forEach(
                    row => {

                        predictionSheet.addRow(
                            row
                        );

                    }
                );


                // =================================================
                // GRAPH IMAGES SHEET
                // =================================================

                const graphSheet =
                    workbook.addWorksheet(
                        "Graphs"
                    );


                graphSheet.getCell(
                    "A1"
                ).value =
                    "Calibration / Prediction Response Graph";


                graphSheet.getCell(
                    "A1"
                ).font = {
                    bold:
                        true,
                    size:
                        14
                };


                // -------------------------------------------------
                // MAIN GRAPH
                // -------------------------------------------------

                const responseSvg =
                    getChartSvg(
                        responseChartRef,
                        responseGraphOptions,
                        1200,
                        520
                    );


                if (
                    responseSvg
                ) {

                    const responsePng =
                        await svgToPngDataUrl(
                            responseSvg,
                            1200,
                            520
                        );


                    const responseImage =
                        workbook.addImage({

                            base64:
                                responsePng,

                            extension:
                                "png"

                        });


                    graphSheet.addImage(
                        responseImage,
                        {
                            tl: {
                                col:
                                    0,
                                row:
                                    2
                            },

                            ext: {
                                width:
                                    1000,
                                height:
                                    430
                            }
                        }
                    );

                }


                // -------------------------------------------------
                // PREDICTION GRAPH
                // -------------------------------------------------

                const predictionSvg =
                    getChartSvg(
                        predictionChartRef,
                        predictionSampleGraphOptions,
                        1200,
                        480
                    );


                if (
                    predictionSvg
                ) {

                    const predictionPng =
                        await svgToPngDataUrl(
                            predictionSvg,
                            1200,
                            480
                        );


                    const predictionImage =
                        workbook.addImage({

                            base64:
                                predictionPng,

                            extension:
                                "png"

                        });


                    graphSheet.addImage(
                        predictionImage,
                        {
                            tl: {
                                col:
                                    0,
                                row:
                                    27
                            },

                            ext: {
                                width:
                                    1000,
                                height:
                                    400
                            }
                        }
                    );

                }


                // =================================================
                // FORMAT SHEETS
                // =================================================

                workbook.worksheets.forEach(
                    sheet => {

                        const header =
                            sheet.getRow(1);

                        header.font = {
                            bold:
                                true
                        };


                        header.alignment = {
                            vertical:
                                "middle"
                        };


                        header.eachCell(
                            cell => {

                                cell.border = {

                                    top: {
                                        style:
                                            "thin"
                                    },

                                    left: {
                                        style:
                                            "thin"
                                    },

                                    bottom: {
                                        style:
                                            "thin"
                                    },

                                    right: {
                                        style:
                                            "thin"
                                    }

                                };

                            }
                        );

                    }
                );


                // =================================================
                // DOWNLOAD
                // =================================================

                const buffer =
                    await workbook.xlsx.writeBuffer();


                const blob =
                    new Blob(
                        [buffer],
                        {
                            type:
                                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        }
                    );


                saveAs(
                    blob,
                    "MicroSlide_Validation_Report.xlsx"
                );

            } catch (error) {

                console.error(
                    "Excel export failed:",
                    error
                );

            }

        };


    // ============================================================
    // EXPORT PDF
    // ============================================================

    const exportPDF =
        async () => {

            try {

                const doc =
                    new jsPDF(
                        "landscape",
                        "mm",
                        "a4"
                    );


                // =================================================
                // TITLE
                // =================================================

                doc.setFontSize(
                    16
                );

                doc.text(
                    "LIMS PORTAL VALIDATION REPORT",
                    14,
                    15
                );


                // =================================================
                // CALIBRATION INPUT
                // =================================================

                doc.setFontSize(
                    13
                );

                doc.text(
                    "Calibration Input",
                    14,
                    24
                );


                autoTable(
                    doc,
                    {

                        startY:
                            29,

                        head: [[

                            "Level",

                            "SAV",

                            "Resp1",

                            "Resp2"

                        ]],

                        body:
                            calibrationInputData.map(
                                row => [

                                    row.Level,

                                    row.SAV,

                                    row.Resp1,

                                    row.Resp2

                                ]
                            ),

                        styles: {
                            fontSize:
                                8
                        },

                        theme:
                            "grid"

                    }
                );


                // =================================================
                // CALIBRATION RESULTS
                // =================================================

                doc.addPage();


                doc.setFontSize(
                    13
                );

                doc.text(
                    "Calibration Results",
                    14,
                    15
                );


                autoTable(
                    doc,
                    {

                        startY:
                            20,

                        head: [[

                            "Parameter",

                            "Value"

                        ]],

                        body:
                            calibrationResultData.map(
                                row => [

                                    row.Parameter,

                                    Array.isArray(
                                        row.Value
                                    )
                                        ? row.Value.join(
                                            ", "
                                        )
                                        : String(
                                            row.Value
                                        )

                                ]
                            ),

                        styles: {
                            fontSize:
                                8
                        },

                        theme:
                            "grid"

                    }
                );


                // =================================================
                // CALIBRATION/PREDICTION GRAPH
                // =================================================

                doc.addPage();


                doc.setFontSize(
                    13
                );

                doc.text(
                    "Response vs SAV / Concentration",
                    14,
                    15
                );


                const responseSvg =
                    getChartSvg(
                        responseChartRef,
                        responseGraphOptions,
                        1200,
                        520
                    );


                if (
                    responseSvg
                ) {

                    const responsePng =
                        await svgToPngDataUrl(
                            responseSvg,
                            1200,
                            520
                        );


                    doc.addImage(
                        responsePng,
                        "PNG",
                        10,
                        22,
                        277,
                        120
                    );

                }


                // =================================================
                // PREDICTION INPUT / OUTPUT
                // =================================================

                doc.addPage();


                doc.setFontSize(
                    13
                );

                doc.text(
                    "Prediction Input / Output",
                    14,
                    15
                );


                autoTable(
                    doc,
                    {

                        startY:
                            20,

                        head: [[

                            "Sample ID",

                            "Resp3",

                            "Resp4",

                            "Concentration"

                        ]],

                        body:
                            predictionExportData.map(
                                row => [

                                    row.SampleID,

                                    row.Resp3,

                                    row.Resp4,

                                    row.Concentration

                                ]
                            ),

                        styles: {
                            fontSize:
                                8
                        },

                        theme:
                            "grid"

                    }
                );


                // =================================================
                // PREDICTION GRAPH
                // =================================================

                doc.addPage();


                doc.setFontSize(
                    13
                );

                doc.text(
                    "Prediction Sample ID vs Concentration",
                    14,
                    15
                );


                const predictionSvg =
                    getChartSvg(
                        predictionChartRef,
                        predictionSampleGraphOptions,
                        1200,
                        480
                    );


                if (
                    predictionSvg
                ) {

                    const predictionPng =
                        await svgToPngDataUrl(
                            predictionSvg,
                            1200,
                            480
                        );


                    doc.addImage(
                        predictionPng,
                        "PNG",
                        10,
                        22,
                        277,
                        115
                    );

                }


                // =================================================
                // SAVE
                // =================================================

                doc.save(
                    "MicroSlide_Validation_Report.pdf"
                );

            } catch (error) {

                console.error(
                    "PDF export failed:",
                    error
                );

            }

        };


    // ============================================================
    // RESULT DISPLAY
    // ============================================================

    // Use the complete calibration API response for the on-screen
    // result table as well. This replaces the old resultFields-only view.
    const resultDisplayRows = useMemo(
        () =>
            calibrationResultData.map(item => ({
                field: item.Parameter,
                value: item.Value
            })),
        [calibrationResultData]
    );


    // ============================================================
    // RENDER
    // ============================================================

    return (

        <Box
            sx={{
                width:
                    "100%"
            }}
        >

            {/* ================================================== */}
            {/* HISTORY */}
            {/* ================================================== */}

            {showHistory && (

                <Paper
                    elevation={2}
                    sx={{
                        p: 2,
                        mb: 2
                    }}
                >

                    <Box
                        sx={{
                            display:
                                "flex",

                            alignItems:
                                "center",

                            justifyContent:
                                "space-between",

                            mb: 1
                        }}
                    >

                        <Typography
                            variant="h6"
                        >
                            Calibration History
                        </Typography>


                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={
                                <DeleteSweepIcon />
                            }
                            disabled={
                                !historyData.length
                            }
                            onClick={() =>
                                setOpenClearDialog(
                                    true
                                )
                            }
                        >
                            Clear History
                        </Button>

                    </Box>


                    <TableContainer>

                        <Table
                            size="small"
                        >

                            <TableHead>

                                <TableRow>

                                    <TableCell>
                                        Run
                                    </TableCell>

                                    <TableCell>
                                        Description
                                    </TableCell>
                                    

                                    {/*<TableCell>*/}
                                    {/*    Prediction Launch*/}
                                    {/*</TableCell>*/}

                                </TableRow>

                            </TableHead>


                            <TableBody>

                                {historyData.map(
                                    (entry, index) => {

                                        const run =
                                            entry?.run ??
                                            entry?.runId ??
                                            entry?.id ??
                                            index + 1;


                                        const isSelected =
                                            String(
                                                run
                                            ) ===
                                            String(
                                                selectedRun
                                            );


                                        return (

                                            <TableRow
                                                key={
                                                    `${run} -${index} `
                                                }

                                                selected={
                                                    isSelected
                                                }

                                                hover

                                                onClick={() =>
                                                    setSelectedRun(
                                                        run
                                                    )
                                                }

                                                sx={{
                                                    cursor:
                                                        "pointer"
                                                }}
                                            >

                                                <TableCell>
                                                    {run}
                                                </TableCell>


                                                <TableCell>
                                                    {
                                                        entry?.description ||
                                                        "-"
                                                    }
                                                </TableCell>


                                                {/*<TableCell>*/}

                                                {/*    <Link*/}
                                                {/*        component="button"*/}
                                                {/*        underline="hover"*/}
                                                {/*        onClick={*/}
                                                {/*            event => {*/}

                                                {/*                event.stopPropagation();*/}

                                                {/*                if (*/}
                                                {/*                    entry?.onReport*/}
                                                {/*                ) {*/}

                                                {/*                    entry.onReport(*/}
                                                {/*                        entry*/}
                                                {/*                    );*/}

                                                {/*                }*/}

                                                {/*            }*/}
                                                {/*        }*/}
                                                {/*    >*/}
                                                {/*        Report*/}
                                                {/*    </Link>*/}

                                                {/*</TableCell>*/}


                                                {/*<TableCell>*/}

                                                {/*    <Link*/}
                                                {/*        component="button"*/}
                                                {/*        underline="hover"*/}
                                                {/*        onClick={*/}
                                                {/*            event => {*/}

                                                {/*                event.stopPropagation();*/}

                                                {/*                handlePredict(*/}
                                                {/*                    entry*/}
                                                {/*                );*/}

                                                {/*            }*/}
                                                {/*        }*/}
                                                {/*    >*/}
                                                {/*        Prediction*/}
                                                {/*    </Link>*/}

                                                {/*</TableCell>*/}

                                            </TableRow>

                                        );

                                    }
                                )}


                                {!historyData.length && (

                                    <TableRow>

                                        <TableCell
                                            colSpan={4}
                                            align="center"
                                        >
                                            No calibration history
                                            available.
                                        </TableCell>

                                    </TableRow>

                                )}

                            </TableBody>

                        </Table>

                    </TableContainer>

                </Paper>

            )}


            {/* ================================================== */}
            {/* CALIBRATION RESULT DISPLAY */}
            {/* ================================================== */}

            {selectedResult && (

                <Paper
                    elevation={2}
                    sx={{
                        p: 2,
                        mb: 2
                    }}
                >

                    <Typography
                        variant="h6"
                        sx={{
                            mb: 1
                        }}
                    >
                        Calibration API Response
                    </Typography>


                    <TableContainer>

                        <Table
                            size="small"
                        >

                            <TableBody>

                                {resultDisplayRows.map(
                                    item => (

                                        <TableRow
                                            key={
                                                item.field
                                            }
                                        >

                                            <TableCell
                                                sx={{
                                                    fontWeight:
                                                        "bold"
                                                }}
                                            >
                                                {item.field}
                                            </TableCell>


                                            <TableCell>

                                                {Array.isArray(
                                                    item.value
                                                )
                                                    ? item.value.join(
                                                        ", "
                                                    )
                                                    : String(
                                                        item.value
                                                    )}

                                            </TableCell>

                                        </TableRow>

                                    )
                                )}

                            </TableBody>

                        </Table>

                    </TableContainer>

                </Paper>

            )}


            {/* ================================================== */}
            {/* EXPORT / GRAPH AREA */}
            {/* ================================================== */}

            {showCalibrationGraph && (

                <Paper
                    elevation={2}
                    sx={{
                        p: 2,
                        mb: 2
                    }}
                >

                    <Box
                        sx={{
                            display:
                                "flex",

                            justifyContent:
                                "flex-end",

                            gap:
                                1,

                            mb:
                                2
                        }}
                    >
                        {graphVisible &&
                            predictionSampleGraphOptions.series.length > 0 && (
                                <>
                                                           <Button
                            variant="outlined"
                            onClick={
                                exportExcel
                            }
                        >
                            Export Excel
                        </Button>


                        <Button
                            variant="outlined"
                            onClick={
                                exportPDF
                            }
                        >
                            Export PDF
                        </Button>


                        <Button
                            variant="outlined"
                            onClick={() =>
                                setGraphVisible(
                                    value =>
                                        !value
                                )
                            }
                        >
                            {graphVisible
                                ? "Hide Graphs"
                                : "Show Graphs"}
                        </Button>
                                </>
                            )}


                    </Box>


                    {/* ========================================== */}
                    {/* MAIN CALIBRATION + PREDICTION GRAPH */}
                    {/* ========================================== */}

                    {graphVisible &&
                        responseSeries.length > 0 && (

                            <Box
                                sx={{
                                    mb:
                                        4
                                }}
                            >

                                <HighchartsReact
                                    ref={
                                        responseChartRef
                                    }

                                    highcharts={
                                        Highcharts
                                    }

                                    options={
                                        responseGraphOptions
                                    }

                                />

                            </Box>

                        )}


                    {/* ========================================== */}
                    {/* PREDICTION SAMPLE GRAPH */}
                    {/* ========================================== */}

                    {graphVisible &&
                        predictionSampleGraphOptions
                            .series
                            .length > 0 && (

                            <Box>

                                <HighchartsReact
                                    ref={
                                        predictionChartRef
                                    }

                                    highcharts={
                                        Highcharts
                                    }

                                    options={
                                        predictionSampleGraphOptions
                                    }

                                />

                            </Box>

                        )}


                    {/* ========================================== */}
                    {/* NO GRAPH DATA */}
                    {/* ========================================== */}

                    {graphVisible &&
                        responseSeries.length === 0 &&
                        predictionSampleGraphOptions
                            .series
                            .length === 0 && (

                            <Typography
                                sx={{
                                    textAlign:
                                        "center",

                                    py:
                                        4
                                }}
                            >
                                No graph data available.
                            </Typography>

                        )}

                </Paper>

            )}


            {/* ================================================== */}
            {/* CLEAR HISTORY */}
            {/* ================================================== */}

            <ConfirmDialog

                open={
                    openClearDialog
                }

                title={
                    "Clear History"
                }

                message={
                    "Are you sure you want to clear all calibration history?"
                }

                onConfirm={
                    clearHistory
                }

                onCancel={() =>
                    setOpenClearDialog(
                        false
                    )
                }

            />

        </Box>

    );

}





